import React from 'react';
import { Smartphone, Monitor, CheckCircle2, Download } from 'lucide-react';
import { Card } from 'shared/components/ui/Card';
import Button from 'shared/components/ui/Button';
import { useTranslation } from 'react-i18next';
import { openExtensionUrl } from 'utils/urlHelpers';
import { URLS } from 'configs';
import './CrossPlatformSection.scss';

// Import logo assets
import logoIcon from '../../../../../../assets/images/logo-icon.svg';
import appStoreIcon from '../../../../../../assets/images/icons/app-store.svg';

const CrossPlatformSection = () => {
  const { t } = useTranslation();

  const benefits = [
    t('crossplatform_benefit_1', 'Seamless sync across all devices'),
    t('crossplatform_benefit_2', 'Native apps for the best performance'),
    t('crossplatform_benefit_3', 'Your wishlists everywhere you shop'),
    t('crossplatform_benefit_4', 'One account, unlimited access'),
  ];

  return (
    <section className='crossplatform-section'>
      {/* Background Elements */}
      <div className='crossplatform-section__bg-wrapper'>
        <div className='crossplatform-section__bg-element crossplatform-section__bg-element--1' aria-hidden='true' />
        <div className='crossplatform-section__bg-element crossplatform-section__bg-element--2' aria-hidden='true' />
      </div>

      <div className='crossplatform-section__container'>
        <div className='crossplatform-section__grid'>
          {/* Left Visual - App Store Mockup */}
          <div className='crossplatform-section__visual'>
            <Card className='crossplatform-section__demo-card'>
              <div className='crossplatform-section__demo-content'>
                {/* App Header */}
                <div className='crossplatform-section__app-header'>
                  <div className='crossplatform-section__app-icon'>
                    <img src={logoIcon} alt='Likiee' className='crossplatform-section__app-icon-img' />
                  </div>
                  <div className='crossplatform-section__app-info'>
                    <h3 className='crossplatform-section__app-name'>Likiee</h3>
                    <p className='crossplatform-section__app-desc'>
                      {t('crossplatform_app_desc', 'Get Likiee for better shopping experience')}
                    </p>
                    <div className='crossplatform-section__app-rating'>
                      <div className='crossplatform-section__stars'>
                        {[...Array(5)].map((_, i) => (
                          <span key={i} className='crossplatform-section__star'>
                            ★
                          </span>
                        ))}
                      </div>
                      <span className='crossplatform-section__rating-value'>4.9</span>
                    </div>
                  </div>
                </div>

                {/* Screenshot Placeholders */}
                <div className='crossplatform-section__screenshots'>
                  <div className='crossplatform-section__screenshot'>
                    <Smartphone className='crossplatform-section__screenshot-icon' />
                  </div>
                  <div className='crossplatform-section__screenshot'>
                    <Smartphone className='crossplatform-section__screenshot-icon' />
                  </div>
                  <div className='crossplatform-section__screenshot'>
                    <Smartphone className='crossplatform-section__screenshot-icon' />
                  </div>
                </div>

                {/* Info Rows */}
                <div className='crossplatform-section__info-rows'>
                  <div className='crossplatform-section__info-row' />
                  <div className='crossplatform-section__info-row crossplatform-section__info-row--full' />
                  <div className='crossplatform-section__info-row crossplatform-section__info-row--short' />
                </div>
              </div>

              {/* Magnifying Glass Effect */}
              <div className='crossplatform-section__magnifier' aria-hidden='true'>
                <div className='crossplatform-section__magnifier-content'>
                  <img src={logoIcon} alt='' className='crossplatform-section__magnifier-logo' />
                </div>
              </div>
            </Card>

            {/* Decorative Dots */}
            <div className='crossplatform-section__dots' aria-hidden='true'>
              {[...Array(16)].map((_, i) => (
                <div key={i} className='crossplatform-section__dot' />
              ))}
            </div>

            {/* Decorative Lines */}
            <div className='crossplatform-section__lines' aria-hidden='true'>
              <svg width='60' height='60' viewBox='0 0 60 60' fill='none'>
                <path d='M0 20 L60 20' stroke='currentColor' strokeWidth='3' />
                <path d='M0 40 L60 40' stroke='currentColor' strokeWidth='3' />
              </svg>
            </div>
          </div>

          {/* Right Content */}
          <div className='crossplatform-section__content'>
            <div className='crossplatform-section__badge'>
              <Monitor className='crossplatform-section__badge-icon' aria-hidden='true' />
              <span className='crossplatform-section__badge-text'>{t('crossplatform_badge', 'Cross-Platform')}</span>
            </div>

            <h2 className='crossplatform-section__title'>
              {t('crossplatform_title_1', 'Stay always synced and connected.')}{' '}
              <span className='crossplatform-section__title--gradient'>
                {t('crossplatform_title_2', 'iOS or Android, MacOS or Windows.')}
              </span>
            </h2>

            <p className='crossplatform-section__description'>
              {t('crossplatform_description', 'A universal solution which works everywhere. Mobile apps coming soon.')}
            </p>

            <div className='crossplatform-section__benefits'>
              {benefits.map((benefit, i) => (
                <div key={i} className='crossplatform-section__benefit'>
                  <CheckCircle2 className='crossplatform-section__benefit-icon' />
                  <span className='crossplatform-section__benefit-text'>{benefit}</span>
                </div>
              ))}
            </div>

            <div className='crossplatform-section__actions'>
              <Button
                variant='primary'
                size='large'
                className='crossplatform-section__btn-primary'
                onClick={openExtensionUrl}
              >
                <Download className='crossplatform-section__btn-icon' />
                {t('crossplatform_cta_install', 'Install plugin')}
              </Button>

              <a
                href={URLS.APP_STORE}
                target='_blank'
                rel='noopener noreferrer'
                className='crossplatform-section__appstore-button'
                aria-label='Download on App Store'
              >
                <img
                  src={appStoreIcon}
                  alt='Download on the App Store'
                  className='crossplatform-section__appstore-badge'
                />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CrossPlatformSection;
